import { useCallback, useEffect, useState } from "react";
import { getClientsByOrganizationAsync } from "../api/ClientApi";
import { getAppointmentsByOrganizationAsync } from "../api/AppointmentApi";
import { getNewInquiryCountAsync } from "../api/InquiryApi";
import { Constants } from "../constants/constants";
import { AppointmentStatus } from "../models/AppointmentModels";
import { monthRange } from "../utils/DateTime";

export interface OrganizationStats {
  totalClients: number;
  appointmentsThisMonth: number;
  earningsThisMonth: number;
  newInquiries: number;
}

interface UseOrganizationStatsReturn {
  stats: OrganizationStats;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

const EMPTY_STATS: OrganizationStats = {
  totalClients: 0,
  appointmentsThisMonth: 0,
  earningsThisMonth: 0,
  newInquiries: 0,
};

export const useOrganizationStats = (
  organizationId: string | undefined,
): UseOrganizationStatsReturn => {
  const [stats, setStats] = useState<OrganizationStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    if (!organizationId || !token) {
      setStats(EMPTY_STATS);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { from, to } = monthRange();

      const [clientsRes, appointmentsRes, inquiriesRes] = await Promise.all([
        getClientsByOrganizationAsync(token, organizationId, {
          page: 1,
          pageSize: 1,
        }),
        getAppointmentsByOrganizationAsync(token, organizationId, {
          page: 1,
          pageSize: 100,
          from,
          to,
        }),
        getNewInquiryCountAsync(token, organizationId),
      ]);

      const appointmentsThisMonth = appointmentsRes.result?.totalCount ?? 0;
      const earningsThisMonth = (appointmentsRes.result?.items ?? [])
        .filter((appointment) => appointment.status === AppointmentStatus.Completed)
        .reduce(
          (sum, appointment) =>
            sum +
            appointment.services.reduce(
              (serviceSum, service) =>
                serviceSum + service.price * service.quantity,
              0,
            ),
          0,
        );

      setStats({
        totalClients: clientsRes.result?.totalCount ?? 0,
        appointmentsThisMonth,
        earningsThisMonth,
        newInquiries: inquiriesRes.result ?? 0,
      });
    } catch (err) {
      console.error(err);
      setError("Failed to load dashboard stats.");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { stats, loading, error, reload };
};